// Custom-question stage draft
// =====================================================
// The custom-questions stage is almost entirely client-side. Picking "Open
// ended" or "Multiple choice" pushes the form straight into the transcript
// without telling the assistant, and skipping that form posts nothing at all —
// so neither step reaches conversation history. On reload the history rebuild
// had no way to know a form was open (or had just been skipped) and the
// recruiter came back to a transcript with no form, no Proceed button and no
// options: a dead end in the middle of the flow.
//
// This records the missing half locally, keyed by the assessment it belongs to:
//
//   status 'open'     — a form is on screen waiting to be filled in; restore it.
//   status 'resolved' — the questions were submitted or skipped; restore the
//                       Proceed CTA so the recruiter can move on.
//
// The record is dropped once the recruiter leaves the stage, so it can never
// resurrect a form for a stage that has already moved on.

// The states this stage can be reached from.
//
// flow_1/flow_2 ask from `technical_competence`, and once the criteria are signed
// off also from `done`. The talent audit runs the same stage under its own state
// name, `review_questions`. Every gate on the stage keys off this set: literal
// `technical_competence` checks scattered through the response and history
// handlers are what kept the stage out of Flow 3 entirely.
const CUSTOM_QUESTION_STATES = new Set(['technical_competence', 'done', 'review_questions']);

/** The talent audit's name for the stage. */
export const AUDIT_CUSTOM_QUESTION_STATE = 'review_questions';

export const isCustomQuestionStage = (state?: string | null): boolean =>
  CUSTOM_QUESTION_STATES.has(state ?? '');

/** Minimal option-pill shape, kept structural so this helper needs no feature imports. */
export interface CustomQuestionOption {
  text: string;
  value: string;
}

/** The option that opens the stage. */
export const ADD_CUSTOM_QUESTIONS_OPTION = 'add_custom_questions';

/**
 * The two choices the entry option opens. flow_2 receives these from the
 * assistant; the audit is answered entirely on the client, so it needs its own
 * copy. Labels match what the assistant sends so both flows read the same.
 */
export const CUSTOM_QUESTION_TYPE_OPTIONS: CustomQuestionOption[] = [
  { text: 'Open ended', value: 'create_open_ended_questions' },
  { text: 'Multiple choice', value: 'create_multiple_choice_questions' },
];

export type CustomQuestionType = 'open_ended' | 'multiple_choice';

/** The transcript component that renders each type's form. */
export type CustomQuestionComponentType = 'addSingleQuestion' | 'addMultipleQuestions';

const STORAGE_KEY = 'precision_pending_custom_question';

export interface CustomQuestionDraft {
  assessmentId: string;
  type: CustomQuestionType;
  status: 'open' | 'resolved';
  /**
   * The option pills that were on screen when the recruiter opened the stage.
   * flow_2 always comes back to the same Proceed CTA, but the audit's way
   * forward is decided by the assistant and differs per audit type, so the only
   * safe thing to put back is what it actually offered. Persisted with the draft
   * so a reload mid-form can restore it too.
   */
  resumeOptions?: CustomQuestionOption[];
}

export const componentTypeForCustomQuestion = (
  type: CustomQuestionType
): CustomQuestionComponentType =>
  type === 'multiple_choice' ? 'addMultipleQuestions' : 'addSingleQuestion';

/** Maps the option value the recruiter clicked to the type of form it opens. */
export const customQuestionTypeForOption = (option: string): CustomQuestionType | null => {
  if (option === 'create_open_ended_questions') return 'open_ended';
  if (option === 'create_multiple_choice_questions') return 'multiple_choice';
  return null;
};

/** Every option this stage owns: the entry point and the two type choices. */
export const isCustomQuestionOption = (option: string): boolean =>
  option === ADD_CUSTOM_QUESTIONS_OPTION || customQuestionTypeForOption(option) !== null;

/**
 * Adds the "Add custom questions" pill to the talent audit's own options.
 *
 * In flow_2 the assistant offers the stage itself; the audit's assistant never
 * does, which is why Flow 3 had no way into custom questions at all. The stage
 * is client-owned anyway — picking a type and filling the form never reach the
 * assistant — so the entry point is added here rather than waited on.
 *
 * Strictly additive: it only ever appends to options the assistant already sent,
 * so a turn that wanted a composer (no options) keeps it, and a turn that does
 * offer the stage is left exactly as it came.
 */
export const withCustomQuestionEntry = <T extends CustomQuestionOption>(
  options: T[] | null | undefined,
  state: string | null | undefined,
  isAudit: boolean
): (T | CustomQuestionOption)[] | null | undefined => {
  if (!isAudit || state !== AUDIT_CUSTOM_QUESTION_STATE) return options;
  if (!options || options.length === 0) return options;
  if (options.some((option) => isCustomQuestionOption(option?.value))) return options;
  return [...options, { text: 'Add custom questions', value: ADD_CUSTOM_QUESTIONS_OPTION }];
};

const readDraft = (): CustomQuestionDraft | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as CustomQuestionDraft;
    if (!draft?.assessmentId) return null;
    if (draft.type !== 'open_ended' && draft.type !== 'multiple_choice') return null;
    if (draft.status !== 'open' && draft.status !== 'resolved') return null;
    // A malformed resumeOptions must not take the whole draft down with it — the
    // form restore matters more than the pills that come back after it.
    const resumeOptions = Array.isArray(draft.resumeOptions)
      ? draft.resumeOptions.filter(
          (option: any) => option && typeof option.text === 'string' && typeof option.value === 'string'
        )
      : [];
    return { ...draft, resumeOptions: resumeOptions.length > 0 ? resumeOptions : undefined };
  } catch {
    clearCustomQuestionDraft();
    return null;
  }
};

const writeDraft = (draft: CustomQuestionDraft): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    /* storage unavailable — the reload restore is best-effort */
  }
};

/** Record that the recruiter opened a custom-question form for this assessment. */
export function openCustomQuestionDraft(
  assessmentId: string | null | undefined,
  type: CustomQuestionType,
  resumeOptions?: CustomQuestionOption[] | null
): void {
  if (!assessmentId) return;
  writeDraft({
    assessmentId: String(assessmentId),
    type,
    status: 'open',
    ...(resumeOptions && resumeOptions.length > 0 ? { resumeOptions } : {}),
  });
}

/**
 * The questions were submitted or skipped. Kept (rather than cleared) so a
 * reload restores the Proceed CTA — a skip posts nothing to the server, so
 * history alone cannot tell that the stage is finished with.
 */
export function resolveCustomQuestionDraft(): void {
  const draft = readDraft();
  if (!draft || draft.status === 'resolved') return;
  writeDraft({ ...draft, status: 'resolved' });
}

/** The draft for this assessment, or null. Scoped by id so it can't leak between conversations. */
export function getCustomQuestionDraft(
  assessmentId: string | null | undefined
): CustomQuestionDraft | null {
  if (!assessmentId) return null;
  const draft = readDraft();
  if (!draft || draft.assessmentId !== String(assessmentId)) return null;
  return draft;
}

export function clearCustomQuestionDraft(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
